import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiPower, FiCpu, FiActivity, FiShield, FiSliders,
  FiLock, FiRefreshCw, FiCheck, FiAlertCircle,
} from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import { ROUTES } from '../constants/constants';
import { adminAiAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import { Select, inputSmClass } from '../components/common/FormControls';
import { SkeletonCard } from '../components/common/Skeleton';

// Screen bundles are code-split. Eager top-level imports dragged recharts (and
// each screen's detail panels) into the first paint of EVERY screen — including
// Overview and Settings, which have no charts. React.lazy defers each bundle
// until its tab is actually opened; the <Suspense> boundary below shows a
// lightweight skeleton while the chunk streams in.
const ProvidersScreen = lazy(() => import('../components/admin/ai/ProvidersScreen'));
const UsageScreen = lazy(() => import('../components/admin/ai/UsageScreen'));
const GovernanceScreen = lazy(() => import('../components/admin/ai/GovernanceScreen'));
const BudgetScreen = lazy(() => import('../components/admin/ai/BudgetScreen'));
const TemplatesScreen = lazy(() => import('../components/admin/ai/TemplatesScreen'));
const AnswerReviewScreen = lazy(() => import('../components/admin/ai/AnswerReviewScreen'));
const TracesScreen = lazy(() => import('../components/admin/ai/TracesScreen'));

// ── tokens ──────────────────────────────────────────────────────────────────
const TEAL = '#145f5f';
const GOLD = '#b8973a';
const INK = '#08111f';
const MUTE = '#7a7060';
const BORDER = '#ddd6c6';

// The six screens of the AI Control Plane. `route` powers the sidebar group;
// `ready` flags which are wired today vs. migrating in.
const SCREENS = {
  overview:   { label: 'Overview',           subhead: 'Live state of the AI runtime — kill-switch, tiers, active routing.', ready: true },
  providers:  { label: 'Providers & Models',  subhead: 'Models, routing per mode, and the default fallback — all in one place.', ready: false },
  usage:      { label: 'Usage & Cost',        subhead: 'Per-request tokens, cached tokens, cost and latency — by mode, provider, and top users.', ready: false },
  budget:     { label: 'Budget & Caps',       subhead: 'Live daily token budget vs. the cap, the per-user cap, and the threshold alerts. Spend history lives in Usage & Cost.', ready: false },
  settings:   { label: 'Settings',            subhead: 'Every resolved setting, live-editable. Changes apply within ~10s, no redeploy.', ready: true },
  governance: { label: 'Governance',          subhead: 'Agent-action policy, live proposals/executions, and DPDP consent & erasure.', ready: false },
  templates:  { label: 'Firm Templates',      subhead: 'Read-only oversight of firm drafting recipes and save-time smoke failures.', ready: false },
  traces: { label: 'Traces', subhead: 'How Vee turns ran: speed, tokens, tool health and how often answers end "not settled". Timings and counts only.', ready: false },
  'answer-review': { label: 'Answer Review',  subhead: 'Replies users marked wrong. Judge each, say what was wrong and what Vee should have said.', ready: false },
};

// Curated grouping of the chat.* keys (modelRouting.* lives on the Routing screen).
const GROUPS = [
  { title: 'Kill switch & tiers', icon: FiPower, keys: ['chat.enabled', 'chat.drafting', 'chat.agent', 'chat.webSearch'] },
  { title: 'Generation', icon: FiCpu, keys: ['chat.provider', 'chat.temperature', 'chat.maxTokens', 'chat.maxToolIterations', 'chat.maxTurnMs'] },
  { title: 'Rate limits', icon: FiActivity, keys: ['chat.rateLimit.perMinute', 'chat.rateLimit.dailyTokenCap', 'chat.rateLimit.globalDailyTokenCap'] },
  // Retention re-shape (2026-07-15): chat retention became the user's auto-delete
  // picker, so chat.persistDays / chat.shortRetentionDays were retired from the
  // backend. Only the Recently-Deleted hold stays an admin knob — it lives in
  // KNOWN_KEYS but was in no group, so it wasn't editable anywhere until now.
  { title: 'Privacy & retention', icon: FiShield, keys: ['chat.recentlyDeletedDays', 'chat.trainingCapture'] },
];

const getByPath = (obj, path) =>
  path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

// Stale-while-revalidate cache key (services/viewCache) + the flatten used to
// derive the editable form from a settings payload (shared by the seed and load).
const AI_CP_KEY = 'admin:aiControlPlane';
const flattenConfig = (config) => {
  const flat = {};
  GROUPS.forEach((g) => g.keys.forEach((k) => { flat[k] = getByPath(config, k); }));
  return flat;
};

// ── atoms ─────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button" role="switch" aria-checked={!!checked} disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-[22px] w-[40px] items-center rounded-full transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{ background: checked ? TEAL : '#cfc7b5' }}
    >
      <span className="inline-block h-[16px] w-[16px] rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? 'translateX(21px)' : 'translateX(3px)' }} />
    </button>
  );
}

// State pill with a live-status dot — reuses the shared Chip primitive so it
// matches the rest of the app, but keeps the dot prefix to read as a status
// indicator (not a category badge).
function Pill({ on, label }) {
  return (
    <Chip tone={on ? TEAL : MUTE} className="!px-2.5 !py-1 gap-1.5 !text-[11px]">
      <span className="w-[7px] h-[7px] rounded-full inline-block" style={{ background: on ? '#1e8a8a' : '#b3a994' }} />
      {label}
    </Chip>
  );
}

// ── page ────────────────────────────────────────────────────────────────────
function AIControlPlanePage() {
  const { screen } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = String(user?.roleName || '').toLowerCase() === 'super_admin';
  const active = SCREENS[screen] ? screen : 'overview';

  // Stale-while-revalidate: this route unmounts on nav, so the settings
  // payload reloaded from zero on return and flashed a skeleton over content
  // just seen. Seed data + the derived form from the shared viewCache.
  const cachedSettings = getView(AI_CP_KEY);
  const [data, setData] = useState(() => cachedSettings ?? null);   // { config, overrides, known }
  const [form, setForm] = useState(() => (cachedSettings ? flattenConfig(cachedSettings.config) : {}));
  const [loading, setLoading] = useState(() => !hasView(AI_CP_KEY));
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  // Unknown sub-path → land on Overview.
  // `routing` was folded into Providers — redirect old bookmarks there.
  useEffect(() => {
    if (screen === 'routing') {
      navigate(ROUTES.ADMIN_AI_PROVIDERS, { replace: true });
    } else if (!SCREENS[screen]) {
      navigate(ROUTES.ADMIN_AI_OVERVIEW, { replace: true });
    }
  }, [screen, navigate]);

  const load = useCallback(async () => {
    if (!hasView(AI_CP_KEY)) setLoading(true);
    try {
      const res = await adminAiAPI.getSettings();
      const payload = res?.data || res;
      setData(payload);
      setView(AI_CP_KEY, payload);
      setForm(flattenConfig(payload.config));
    } catch (err) {
      // Keep the cached settings on a failed refresh; banner only on first load.
      if (!hasView(AI_CP_KEY)) setBanner({ kind: 'err', text: err?.message || 'Failed to load AI settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Only the data-backed screens need the settings payload.
  useEffect(() => {
    if (active === 'overview' || active === 'settings') load();
    else setLoading(false);
  }, [active, load]);

  const dirtyKeys = useMemo(() => {
    if (!data) return [];
    return Object.keys(form).filter((k) => form[k] !== getByPath(data.config, k));
  }, [form, data]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!dirtyKeys.length) return;
    setSaving(true); setBanner(null);
    try {
      const updates = {};
      dirtyKeys.forEach((k) => { updates[k] = form[k]; });
      await adminAiAPI.updateSettings({ updates });
      await load();
      setBanner({ kind: 'ok', text: `Saved ${dirtyKeys.length} change${dirtyKeys.length > 1 ? 's' : ''} — live within ~10s.` });
    } catch (err) {
      setBanner({ kind: 'err', text: err?.message || 'Save failed' });
    } finally { setSaving(false); }
  };

  const resetKey = async (key) => {
    setSaving(true); setBanner(null);
    try {
      await adminAiAPI.updateSettings({ clear: [key] });
      await load();
      setBanner({ kind: 'ok', text: `Reset "${key}" to the env default.` });
    } catch (err) {
      setBanner({ kind: 'err', text: err?.message || 'Reset failed' });
    } finally { setSaving(false); }
  };

  const meta = SCREENS[active];
  const overrideCount = data ? Object.keys(data.overrides || {}).length : 0;

  return (
    <AppLayout pageTitle={`AI Control Plane · ${meta.label}`}>
      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: 'rgba(20,95,95,.1)', color: TEAL }}>AI Control Plane</span>
              {overrideCount > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(184,151,58,.16)', color: GOLD }}>{overrideCount} override{overrideCount > 1 ? 's' : ''}</span>
              )}
            </div>
            <h1 className="text-[28px] font-bold" style={{ color: INK, fontFamily: "'Cormorant Garamond', serif" }}>{meta.label}</h1>
            <p className="text-[13px]" style={{ color: MUTE }}>{meta.subhead}</p>
          </div>
          {(active === 'overview' || active === 'settings') && (
            <Button variant="outline" onClick={load} disabled={loading || saving}>
              <FiRefreshCw className={`inline mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          )}
        </div>

        {banner && (
          <div className="mb-4 flex items-center gap-2 px-3.5 py-2.5 rounded-[10px] text-[12.5px] font-medium border"
            style={banner.kind === 'ok'
              ? { background: 'rgba(20,95,95,.07)', borderColor: 'rgba(20,95,95,.25)', color: TEAL }
              : { background: 'rgba(180,105,14,.07)', borderColor: 'rgba(180,105,14,.3)', color: '#b4690e' }}>
            {banner.kind === 'ok' ? <FiCheck /> : <FiAlertCircle />} {banner.text}
          </div>
        )}

        {/* Screen body */}
        {loading && (active === 'overview' || active === 'settings') ? (
          active === 'overview' ? (
            <div className="space-y-4">
              <SkeletonCard rows={2} className="!rounded-[16px]" />
              <SkeletonCard rows={5} />
            </div>
          ) : (
            <div className="space-y-4">
              <SkeletonCard rows={1} headerHeight={10} className="!rounded-[12px]" />
              <SkeletonCard rows={3} />
              <SkeletonCard rows={5} />
              <SkeletonCard rows={2} />
              <SkeletonCard rows={3} />
            </div>
          )
        ) : (
          <>
            {active === 'overview' && (
              data ? <OverviewPanel config={data.config} overrideCount={overrideCount} onTune={() => navigate(ROUTES.ADMIN_AI_SETTINGS)} />
                   : <LoadError onRetry={load} />
            )}
            {active === 'settings' && (
              data ? (
                <SettingsPanel
                  known={data.known} overrides={data.overrides} form={form} config={data.config}
                  isSuperAdmin={isSuperAdmin} dirtyKeys={dirtyKeys} saving={saving}
                  onField={setField} onSave={save} onDiscard={load} onResetKey={resetKey}
                />
              ) : <LoadError onRetry={load} />
            )}
            {(active === 'providers' || active === 'usage' || active === 'budget'
              || active === 'governance' || active === 'templates' || active === 'answer-review' || active === 'traces') && (
              <Suspense fallback={<ScreenFallback />}>
                {active === 'providers' && <ProvidersScreen />}
                {active === 'usage' && <UsageScreen />}
                {active === 'budget' && <BudgetScreen />}
                {active === 'governance' && <GovernanceScreen isSuperAdmin={isSuperAdmin} />}
                {active === 'templates' && <TemplatesScreen />}
                {active === 'answer-review' && <AnswerReviewScreen />}
                {active === 'traces' && <TracesScreen />}
              </Suspense>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

// Suspense fallback while a lazily-loaded screen bundle streams in. Uses the
// same brand skeleton primitives as the Overview/Settings first-load state so
// the swap-in doesn't flash a different loading style.
function ScreenFallback() {
  return (
    <div className="space-y-4">
      <SkeletonCard rows={2} className="!rounded-[16px]" />
      <SkeletonCard rows={5} />
    </div>
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="py-16 text-center">
      <FiAlertCircle className="mx-auto mb-2 text-[22px]" style={{ color: '#b4690e' }} />
      <div className="text-[13px] mb-3" style={{ color: MUTE }}>Couldn’t load AI settings.</div>
      <Button variant="outline" onClick={onRetry}><FiRefreshCw className="inline mr-1.5" />Retry</Button>
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────
// At-a-glance only: kill-switch + tier pills + current routing snapshot.
// All numeric runtime values (tokens, rate, temperature, retention) live in
// Settings where they're editable — duplicating them here just got stale.
function OverviewPanel({ config, overrideCount, onTune }) {
  const c = config.chat || {};
  const routing = config.modelRouting || {};
  const ROUTING_MODES = [
    { key: 'oracle',   label: 'Oracle' },
    { key: 'agent',    label: 'Agent' },
    { key: 'chat',     label: 'Chat' },
    { key: 'drafting', label: 'Drafting' },
    { key: 'vision',   label: 'Vision' },
  ];
  return (
    <div>
      <div className="rounded-[16px] border p-5 mb-4" style={{ borderColor: BORDER, background: c.enabled ? 'rgba(20,95,95,.04)' : 'rgba(180,105,14,.05)' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-[42px] h-[42px] rounded-[12px] flex items-center justify-center" style={{ background: c.enabled ? 'rgba(20,95,95,.12)' : 'rgba(180,105,14,.14)' }}>
              <FiPower className="text-[20px]" style={{ color: c.enabled ? TEAL : '#b4690e' }} />
            </div>
            <div>
              <div className="text-[15px] font-bold" style={{ color: INK }}>ComplyV AI is {c.enabled ? 'ONLINE' : 'OFFLINE'}</div>
              <div className="text-[12px]" style={{ color: MUTE }}>{c.enabled ? 'Serving chat, drafting and agent requests.' : 'Master kill-switch engaged — /api/ai/chat returns 501.'}</div>
            </div>
          </div>
          <Button variant="outline" onClick={onTune}><FiSliders className="inline mr-1.5" />Tune settings</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Pill on={c.enabled} label={`Chat ${c.enabled ? 'on' : 'off'}`} />
          <Pill on={c.drafting} label={`Drafting ${c.drafting ? 'on' : 'off'}`} />
          <Pill on={c.agent} label={`Agent ${c.agent ? 'on' : 'off'}`} />
        </div>
      </div>

      <div className="rounded-[16px] border bg-white/40 overflow-hidden" style={{ borderColor: BORDER }}>
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b" style={{ borderColor: BORDER, background: 'rgba(240,236,228,.6)' }}>
          <div className="flex items-center gap-2">
            <FiCpu className="text-[14px]" style={{ color: TEAL }} />
            <span className="text-[12.5px] font-bold" style={{ color: INK }}>Active routing</span>
          </div>
          <span className="text-[11px]" style={{ color: MUTE }}>
            Default provider: <span style={{ color: INK, fontWeight: 600 }}>{c.provider || '—'}</span>
          </span>
        </div>
        <div className="divide-y" style={{ borderColor: BORDER }}>
          {ROUTING_MODES.map((m) => {
            const assigned = routing[m.key];
            return (
              <div key={m.key} className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: `1px solid ${BORDER}` }}>
                <span className="text-[12.5px] font-semibold" style={{ color: INK }}>{m.label}</span>
                {assigned ? (
                  <span className="text-[12px]" style={{ color: INK }}>{assigned}</span>
                ) : (
                  <span className="text-[11px] italic" style={{ color: MUTE }}>using default ({c.provider || '—'})</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// "Currently in effect" condensed strip — read-only summary of the runtime
// values that previously duplicated themselves on Overview. Sits atop the
// editable groups so admins see the live state without scanning every row.
function SettingsSummaryStrip({ config }) {
  const c = config?.chat || {};
  const rl = c.rateLimit || {};
  const items = [
    { label: 'Provider',         value: c.provider || '—' },
    { label: 'Max tokens',       value: c.maxTokens ?? '—' },
    { label: 'Rate / min',       value: rl.perMinute ?? '—' },
    { label: 'Daily cap',        value: rl.dailyTokenCap ? rl.dailyTokenCap.toLocaleString() : 'none' },
    { label: 'Temperature',      value: c.temperature ?? '—' },
    { label: 'Tool rounds',      value: c.maxToolIterations ?? '—' },
    { label: 'Training capture', value: c.trainingCapture || 'off' },
    // Live retention control is the user's auto-delete picker; the only
    // admin-tunable retention knob left is the Recently-Deleted hold.
    { label: 'Recently-deleted hold', value: `${c.recentlyDeletedDays ?? '—'}d` },
  ];
  return (
    <div className="rounded-[12px] border bg-white/40 px-3 py-2 mb-4 flex flex-wrap gap-x-5 gap-y-1.5" style={{ borderColor: BORDER }}>
      <span className="text-[10.5px] uppercase tracking-wide font-semibold self-center" style={{ color: MUTE }}>Currently in effect</span>
      {items.map((t) => (
        <div key={t.label} className="flex items-baseline gap-1.5">
          <span className="text-[10.5px]" style={{ color: MUTE }}>{t.label}</span>
          <span className="text-[12.5px] font-bold" style={{ color: INK }}>{String(t.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────
function SettingsPanel({ known, overrides, form, config, isSuperAdmin, dirtyKeys, saving, onField, onSave, onDiscard, onResetKey }) {
  const renderControl = (key) => {
    const spec = known[key] || {};
    const locked = spec.sensitive && !isSuperAdmin;
    const val = form[key];

    if (spec.type === 'boolean') return <Toggle checked={!!val} disabled={locked} onChange={(v) => onField(key, v)} />;
    if (spec.type === 'enum') {
      return (
        <div className="w-[170px]">
          <Select
            size="sm"
            value={val ?? ''}
            onChange={(v) => onField(key, v)}
            options={(spec.values || []).map((v) => ({ value: v, label: v }))}
            disabled={locked}
          />
        </div>
      );
    }
    if (spec.type === 'int' || spec.type === 'float') {
      return (
        <input
          type="number" value={val ?? ''} disabled={locked} min={spec.min} max={spec.max}
          step={spec.type === 'float' ? 0.1 : 1}
          onChange={(e) => onField(key, e.target.value === '' ? '' : (spec.type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value, 10)))}
          className={`${inputSmClass} w-[110px] text-right disabled:opacity-50`}
        />
      );
    }
    return (
      <input
        type="text" value={val ?? ''} disabled={locked}
        onChange={(e) => onField(key, e.target.value)}
        className={`${inputSmClass} w-[170px] disabled:opacity-50`}
      />
    );
  };

  return (
    <div>
      <SettingsSummaryStrip config={config} />
      {dirtyKeys.length > 0 && (
        <div className="flex items-center justify-end gap-2 mb-3">
          <Button variant="ghost" onClick={onDiscard} disabled={saving}>Discard</Button>
          <Button onClick={onSave} disabled={saving}>{saving ? 'Saving…' : `Save ${dirtyKeys.length} change${dirtyKeys.length > 1 ? 's' : ''}`}</Button>
        </div>
      )}

      <div className="space-y-4">
        {GROUPS.map((g) => {
          const Icon = g.icon;
          return (
            <div key={g.title} className="rounded-[16px] border bg-white/40 overflow-hidden" style={{ borderColor: BORDER }}>
              <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: BORDER, background: 'rgba(240,236,228,.6)' }}>
                <Icon className="text-[14px]" style={{ color: TEAL }} />
                <span className="text-[12.5px] font-bold" style={{ color: INK }}>{g.title}</span>
              </div>
              <div>
                {g.keys
                  // Drift-guard: a key that is neither in the backend `known`
                  // schema nor present on the resolved config is a retired key
                  // (e.g. chat.persistDays after the 2026-07-15 retention
                  // reshape) — skip it so it can never render as a blank input.
                  // A key with a real `known` spec still renders even when its
                  // value is empty (an unset env default is legitimate).
                  .filter((key) => known[key] || getByPath(config, key) != null)
                  .map((key, i) => {
                  const spec = known[key] || {};
                  const isOverridden = !!(overrides && overrides[key]);
                  const locked = spec.sensitive && !isSuperAdmin;
                  const dirty = dirtyKeys.includes(key);
                  return (
                    <div key={key} className="flex items-center justify-between gap-3 px-4 py-3"
                      style={{ borderTop: i === 0 ? 'none' : `1px solid ${BORDER}`, background: dirty ? 'rgba(184,151,58,.06)' : 'transparent' }}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-semibold" style={{ color: INK }}>{spec.label || key}</span>
                          {spec.sensitive && (
                            <Chip bg="rgba(180,105,14,.12)" color="#b4690e" className="gap-1">
                              <FiLock className="text-[9px]" />super admin
                            </Chip>
                          )}
                          {isOverridden
                            ? <Chip tone={GOLD}>overridden</Chip>
                            : <Chip>env default</Chip>}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: MUTE, fontFamily: "'JetBrains Mono', monospace" }}>
                          {key}{spec.min != null ? ` · ${spec.min}–${spec.max}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isOverridden && !locked && (
                          <Button
                            variant="ghost" size="xs"
                            onClick={() => onResetKey(key)}
                            title="Reset to env default"
                          >
                            Reset
                          </Button>
                        )}
                        {renderControl(key)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AIControlPlanePage;
